import { useCallback, useEffect, useMemo, useState } from 'react';
import { localStorageBridge, useNavigator } from 'shared/src/utils';
import { useGoodsSno } from './useGoodsSno';
import { useGoodsFrom } from './useGoodsFrom';
import { useAffiliateAiContentEligibility } from '../queries/useAffiliateAiContentEligibility';
import { AI_CONTENT_DRAFT_ENTRY_FROM_SCREENS } from '../constant/aiContentEntryFromScreens';
import { useLogClickAiContentDraftBanner } from './useLogClickAiContentDraftBanner';

// X 클릭 시 하루간 미노출. 닫은 시각(ms)을 저장해 24시간 이내면 숨긴다.
const DISMISS_STORAGE_KEY = 'AFFILIATE_AI_CONTENT_DRAFT_ENTRY_DISMISSED_AT';
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const ELIGIBLE_ENTRY_FROM_SCREENS: readonly string[] =
  AI_CONTENT_DRAFT_ENTRY_FROM_SCREENS;

/**
 * 상품상세 하단 'AI 콘텐츠 초안 만들기' 진입점 노출/동작 훅.
 *
 * 노출 조건:
 * - '온보딩 미션, 추천 리워드 상품' 진입점(FROM)을 통해 진입한 경우에만 노출
 * - eligibility API의 is_eligible 이 true 인 경우에만 노출
 *   (카탈로그 제외 상품 등은 서버 is_eligible 에서 걸러진다)
 * - X 클릭 후 하루(24h) 동안 미노출
 */
export const useAiContentDraftFloating = () => {
  const goodsSno = useGoodsSno();
  const from = useGoodsFrom();
  const { navigator } = useNavigator();
  const logClickBanner = useLogClickAiContentDraftBanner();

  const isEligibleEntry = useMemo(
    () => from != null && ELIGIBLE_ENTRY_FROM_SCREENS.includes(from),
    [from],
  );

  // null = 로컬스토리지 조회 중, false = 노출 가능, true = 하루 이내 닫음
  const [dismissedWithinDay, setDismissedWithinDay] = useState<boolean | null>(
    null,
  );
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    if (!isEligibleEntry) return;

    let active = true;
    localStorageBridge
      .getString(DISMISS_STORAGE_KEY)
      .then(raw => {
        if (!active) return;
        const dismissedAt = raw != null ? Number(raw) : NaN;
        const within =
          Number.isFinite(dismissedAt) && Date.now() - dismissedAt < ONE_DAY_MS;
        setDismissedWithinDay(within);
      })
      .catch(() => {
        if (active) setDismissedWithinDay(false);
      });

    return () => {
      active = false;
    };
  }, [isEligibleEntry]);

  const { data } = useAffiliateAiContentEligibility({
    enabled: isEligibleEntry && dismissedWithinDay === false,
  });

  const showFloating = useMemo(
    () =>
      isEligibleEntry &&
      dismissedWithinDay === false &&
      !closed &&
      data?.is_eligible === true,
    [isEligibleEntry, dismissedWithinDay, closed, data?.is_eligible],
  );

  const onClickFloating = useCallback(() => {
    logClickBanner();
    navigator.moveToWebview(
      `/affiliate/ai-content-draft/goods/${goodsSno}/loading`,
    );
  }, [logClickBanner, navigator, goodsSno]);

  const onCloseFloating = useCallback(() => {
    setClosed(true);
    localStorageBridge.putString(DISMISS_STORAGE_KEY, String(Date.now()));
  }, []);

  return { showFloating, onClickFloating, onCloseFloating };
};
